import { memo } from 'react';
import { 
  TrendingUp, 
  Users, 
  Target, 
  DollarSign, 
  TrendingDown,
  Globe,
  UserCheck,
  Phone,
  ShoppingCart
} from 'lucide-react';
import MetricCard from '../../common/MetricCard';

const KeyMetricCard = ({ label, value, subtitle, icon: Icon, trend }) => (
  <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm font-medium text-gray-600">{label}</p>
        <p className="text-3xl font-bold text-gray-900 mt-2">
          {typeof value === 'number' ? value.toLocaleString() : value}
        </p>
        {subtitle && (
          <p className="text-sm text-gray-500 mt-1">{subtitle}</p>
        )}
      </div>
      {Icon && (
        <div className="p-3 bg-blue-50 rounded-lg">
          <Icon className="w-6 h-6 text-blue-600" />
        </div>
      )}
    </div>
    {trend && (
      <div className="mt-4 flex items-center text-sm">
        {trend > 0 ? (
          <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
        ) : (
          <TrendingDown className="w-4 h-4 text-red-500 mr-1" />
        )}
        <span className={trend > 0 ? 'text-green-600' : 'text-red-600'}>
          {Math.abs(trend)}% vs last period
        </span>
      </div>
    )}
  </div>
);

export const GrowthEquation = memo(({ data, loading, error }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
        <p className="font-semibold">Error loading growth equation data</p>
        <p className="text-sm mt-1">{error}</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center text-gray-600">
        No data available
      </div>
    );
  }

  const { funnel, conversionRates, metrics } = data;

  return (
    <div className="space-y-6">
      {/* Main Layout: Left (Funnel) + Right (Key Metrics) */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Left Side: Funnel and Conversion Metrics (4/5 width) */}
        <div className="lg:col-span-4 space-y-6">
          {/* Row 1: Funnel Stages - 4 cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard
              title="Strangers"
              value={funnel.strangers.toLocaleString()}
              icon={Users}
              subtitle={`${(conversionRates.strangersToVisitors * 100).toFixed(1)}% → Visitors`}
            />
            
            <MetricCard
              title="Visitors"
              value={funnel.visitors.toLocaleString()}
              icon={Globe}
              subtitle={`${(conversionRates.visitorsToLeads * 100).toFixed(1)}% → Leads`}
              change={((funnel.visitors - funnel.strangers * 0.55) / (funnel.strangers * 0.55)) * 100}
              changeLabel="vs previous"
            />
            
            <MetricCard
              title="Leads (SQLs)"
              value={funnel.sqls.toLocaleString()}
              icon={Phone}
              subtitle={`${(conversionRates.sqlsToBuyers * 100).toFixed(0)}% show up rate`}
              tooltip="Sales Qualified Leads - Booked Calls"
            />
            
            <MetricCard
              title="Buyers"
              value={funnel.buyers.toLocaleString()}
              icon={ShoppingCart}
              subtitle={`${(conversionRates.closingRate * 100).toFixed(0)}% close rate`}
              change={15.2}
              changeLabel="vs last year"
              lastYearLabel="2024"
              lastYearValue={funnel.buyers - 2}
            />
          </div>

          {/* Row 2: Conversion Metrics */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h4 className="font-semibold text-gray-900 mb-4">Conversion Rates</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-gray-600">Strangers → Visitors</p>
                <p className="text-lg font-semibold text-gray-900">
                  {(conversionRates.strangersToVisitors * 100).toFixed(1)}%
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Visitors → Leads</p>
                <p className="text-lg font-semibold text-gray-900">
                  {(conversionRates.visitorsToLeads * 100).toFixed(1)}%
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Leads → SQLs</p>
                <p className="text-lg font-semibold text-gray-900">
                  {(conversionRates.leadsToSQLs * 100).toFixed(1)}%
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Show Up Rate</p>
                <p className="text-lg font-semibold text-gray-900">
                  {(conversionRates.sqlsToBuyers * 100).toFixed(0)}%
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Closing Rate</p>
                <p className="text-lg font-semibold text-gray-900">
                  {(conversionRates.closingRate * 100).toFixed(0)}%
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Overall Conversion</p>
                <p className="text-lg font-semibold text-gray-900">
                  {((funnel.buyers / funnel.strangers) * 100).toFixed(3)}%
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side: Key Metrics Column (1/5 width) */}
        <div className="space-y-4">
          <KeyMetricCard
            label="Lifetime Value (LTV)"
            value={`$${metrics.ltv.toLocaleString()}`}
            icon={DollarSign}
            trend={5.2}
          />
          <KeyMetricCard
            label="Average Sale"
            value={`$${metrics.avgSale.toLocaleString()}`}
            icon={Target}
          />
          <KeyMetricCard
            label="Cost Per Acquisition (CPA)"
            value={`$${metrics.cpa.toLocaleString()}`}
            icon={TrendingDown}
          />
          <KeyMetricCard
            label="ROAS"
            value={`${metrics.roas.toFixed(1)}x`}
            subtitle="Return on Ad Spend"
            icon={TrendingUp}
            trend={8.3}
          />
        </div>
      </div>
    </div>
  );
});

GrowthEquation.displayName = 'GrowthEquation';